/**
 * Канбан-доска.
 *
 * Колонки — значения одного поля-перечисления, перетаскивание карточки меняет
 * это значение. Никакой особой «операции доски» не существует: перенос — то же
 * присваивание, что и из карточки, поэтому права и сценарии срабатывают так же.
 *
 * Перетаскивание продублировано клавиатурой: карточку можно взять пробелом,
 * перевести стрелками и отпустить — с объявлениями в aria-live. Мышь не должна
 * быть единственным способом работать.
 */

import * as React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronRight } from "lucide-react";

import type { FilterDraft } from "@/modules/views/FilterEditor";
import type { TaskRow } from "@/shared/api/tasks";
import * as views from "@/shared/api/views";
import { cn } from "@/shared/lib/cn";
import { counted } from "@/shared/lib/plural";
import { Badge, statusDot } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/Feedback";
import { useToast } from "@/shared/ui/Toast";

const STEP = 20;

/** Ожидаемая высота карточки вместе с отступом: уточняется замером. */
const CARD_H = 72;

/** Сколько карточек держать за краем окна, чтобы прокрутка не мигала пустотой. */
const OVERSCAN = 6;

export interface BoardColumnSpec {
  value: string | number;
  label: string;
  /** Цвет статуса, если колонки разложены по статусам. */
  color?: string;
  /** Предел незавершённой работы: превышение подсвечивается, но не запрещается. */
  wip?: number;
}

export interface BoardViewProps {
  filter: FilterDraft;
  columnField: string;
  columns: BoardColumnSpec[];
  onOpenTask: (taskId: number) => void;
  query?: string;
}

/**
 * Доска с карточкой, переставленной в другую колонку.
 *
 * Считается на клиенте только то, что видно глазом: сама карточка и счётчики.
 * Порядок внутри колонки и «есть ещё» остаются за сервером — их подтвердит
 * перечитывание.
 */
function moved(
  board: views.Board | undefined,
  taskId: number,
  value: string | number,
): views.Board | undefined {
  if (!board) {
    return board;
  }
  const card = board.columns.flatMap((column) => column.items).find((item) => item.id === taskId);
  if (!card) {
    return board;
  }
  return {
    ...board,
    columns: board.columns.map((column) => {
      const had = column.items.some((item) => item.id === taskId);
      const takes = column.value === value;
      if (had === takes) {
        return column;
      }
      return takes
        ? { ...column, items: [card, ...column.items], total: column.total + 1 }
        : {
            ...column,
            items: column.items.filter((item) => item.id !== taskId),
            total: Math.max(0, column.total - 1),
          };
    }),
  };
}

export function BoardView({
  filter,
  columnField,
  columns,
  onOpenTask,
  query,
}: BoardViewProps): React.ReactElement {
  const client = useQueryClient();
  const toast = useToast();
  const [perColumn, setPerColumn] = React.useState(STEP);
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  const [dragging, setDragging] = React.useState<number | null>(null);
  // Объявления читаются голосом: без них перенос с клавиатуры молчалив
  const [announcement, setAnnouncement] = React.useState("");

  const request: views.BoardRequest = {
    column_field: columnField,
    values: columns.map((column) => column.value),
    per_column: perColumn,
    conditions: filter.conditions,
    logic: filter.logic,
    ...(query ? { q: query } : {}),
  };

  const board = useQuery({
    queryKey: ["board", request],
    queryFn: () => views.taskBoard(request),
  });

  const key = ["board", request];

  const move = useMutation({
    mutationFn: (input: { taskId: number; value: string | number; label: string; name: string }) =>
      views.moveCard(input.taskId, columnField, input.value),
    onMutate: async (input) => {
      // Карточка обязана оказаться в новой колонке сразу: перетаскивание —
      // частая операция (CLAUDE.md), а до ответа сервера проходит четверть
      // секунды, и всё это время карточка отскакивала назад
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<views.Board>(key);
      client.setQueryData<views.Board>(key, (board) => moved(board, input.taskId, input.value));
      return { previous };
    },
    onSuccess: (_result, input) => {
      setAnnouncement(`«${input.name}» перенесена в колонку «${input.label}»`);
    },
    onError: (error: Error, _input, context) => {
      // Возврат ровно к тому, что было: доска — общий экран, и придумывать
      // на ней своё состояние нельзя
      if (context?.previous) {
        client.setQueryData(key, context.previous);
      }
      toast.show({ tone: "danger", title: "Карточка не перенесена", description: error.message });
    },
    onSettled: () => {
      // Сверка с сервером в любом случае: счётчики колонок и порядок карточек
      // считает он
      void client.invalidateQueries({ queryKey: ["board"] });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const byValue = new Map(
    (board.data?.columns ?? []).map((column) => [String(column.value), column]),
  );
  const titles = new Map(columns.map((column) => [String(column.value), column.label]));

  const announcements: Announcements = {
    onDragStart: ({ active }) => `Карточка ${String(active.id)} взята`,
    onDragOver: ({ over }) =>
      over ? `Над колонкой «${titles.get(String(over.id)) ?? ""}»` : "Вне колонок",
    onDragEnd: ({ over }) =>
      over
        ? `Карточка перенесена в колонку «${titles.get(String(over.id)) ?? ""}»`
        : "Перенос отменён",
    onDragCancel: () => "Перенос отменён",
  };

  const handleEnd = (event: DragEndEvent): void => {
    setDragging(null);
    const target = event.over?.id;
    if (target === undefined) {
      return;
    }
    const column = columns.find((item) => String(item.value) === String(target));
    if (!column) {
      return;
    }
    const card = (board.data?.columns ?? [])
      .flatMap((item) => item.items)
      .find((item) => item.id === Number(event.active.id));
    move.mutate({
      taskId: Number(event.active.id),
      value: column.value,
      label: column.label,
      name: card?.name ?? "Карточка",
    });
  };

  const handleStart = (event: DragStartEvent): void => setDragging(Number(event.active.id));

  /** Клавиатурная замена перетаскиванию: Alt+стрелка двигает карточку по колонкам. */
  const shift = (task: TaskRow, from: string, direction: -1 | 1): void => {
    const index = columns.findIndex((item) => String(item.value) === from);
    const target = columns[index + direction];
    if (!target) {
      setAnnouncement("Дальше колонок нет");
      return;
    }
    move.mutate({ taskId: task.id, value: target.value, label: target.label, name: task.name });
  };

  if (board.isPending) {
    return (
      <p className="text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">Загрузка доски…</p>
    );
  }

  if (columns.length === 0) {
    return (
      <EmptyState
        title="Колонок нет"
        description="Выберите поле-перечисление, по которому раскладывать доску."
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      accessibility={{ announcements }}
      onDragStart={handleStart}
      onDragEnd={handleEnd}
    >
      <div className="flex h-full min-h-0 gap-[var(--pt-space-12)] overflow-x-auto pb-[var(--pt-space-8)]">
        {columns.map((column) => {
          const key = String(column.value);
          const data = byValue.get(key);
          const isCollapsed = collapsed.has(key);
          return (
            <BoardColumn
              key={key}
              spec={column}
              total={data?.total ?? 0}
              items={data?.items ?? []}
              hasMore={data?.has_more ?? false}
              collapsed={isCollapsed}
              dragging={dragging}
              onToggle={() =>
                setCollapsed((current) => {
                  const next = new Set(current);
                  if (next.has(key)) {
                    next.delete(key);
                  } else {
                    next.add(key);
                  }
                  return next;
                })
              }
              onOpenTask={onOpenTask}
              onShift={(task, direction) => shift(task, key, direction)}
              onMore={() => setPerColumn((value) => value + STEP)}
            />
          );
        })}
      </div>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </DndContext>
  );
}

interface BoardColumnProps {
  spec: BoardColumnSpec;
  total: number;
  items: TaskRow[];
  hasMore: boolean;
  collapsed: boolean;
  dragging: number | null;
  onToggle: () => void;
  onOpenTask: (taskId: number) => void;
  onShift: (task: TaskRow, direction: -1 | 1) => void;
  onMore: () => void;
}

function BoardColumn({
  spec,
  total,
  items,
  hasMore,
  collapsed,
  dragging,
  onToggle,
  onOpenTask,
  onShift,
  onMore,
}: BoardColumnProps): React.ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: String(spec.value) });
  const overWip = spec.wip !== undefined && total > spec.wip;
  const scroller = React.useRef<HTMLDivElement | null>(null);

  // Карточек в колонке бывают сотни: в DOM держим только видимые, иначе
  // прокрутка доски начинает спотыкаться там, где работы больше всего
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => CARD_H,
    overscan: OVERSCAN,
  });

  if (collapsed) {
    return (
      <section
        ref={setNodeRef}
        className={cn(
          "flex w-[44px] shrink-0 flex-col items-center gap-[var(--pt-space-8)] rounded-[var(--pt-radius-3)]",
          "border border-[var(--pt-border-default)] bg-[var(--pt-bg-sunken)] py-[var(--pt-space-8)]",
          isOver && "border-[var(--pt-accent-border)]",
        )}
      >
        <button type="button" onClick={onToggle} aria-label={`Развернуть «${spec.label}»`}>
          <ChevronRight className="size-[var(--pt-icon)]" />
        </button>
        <span className="[writing-mode:vertical-rl] text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">
          {spec.label} · {total}
        </span>
      </section>
    );
  }

  return (
    <section
      ref={setNodeRef}
      aria-label={`Колонка «${spec.label}»`}
      className={cn(
        "flex w-[280px] shrink-0 flex-col rounded-[var(--pt-radius-3)] border",
        "border-[var(--pt-border-default)] bg-[var(--pt-bg-sunken)]",
        isOver && "border-[var(--pt-accent-border)] bg-[var(--pt-bg-hover)]",
      )}
    >
      <header className="flex items-center gap-[var(--pt-space-8)] px-[var(--pt-space-8)] py-[var(--pt-space-8)]">
        <button type="button" onClick={onToggle} aria-label={`Свернуть «${spec.label}»`}>
          <ChevronDown className="size-[var(--pt-icon)]" />
        </button>
        <span
          aria-hidden
          className="size-[8px] shrink-0 rounded-full"
          style={{ background: statusDot(spec.color) }}
        />
        <span className="min-w-0 flex-1 truncate text-[var(--pt-font-ui)] font-medium">
          {spec.label}
        </span>
        <Badge tone={overWip ? "danger" : "neutral"}>
          {spec.wip === undefined ? total : `${String(total)} / ${String(spec.wip)}`}
        </Badge>
      </header>

      <div
        ref={scroller}
        className="min-h-0 flex-1 overflow-y-auto px-[var(--pt-space-8)] pb-[var(--pt-space-8)]"
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {/* Высота списка — сумма всех карточек, включая невидимые: без неё
              полоса прокрутки показывала бы только отрисованный кусок */}
          <ul className="relative" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((row) => {
              const task = items[row.index];
              if (task === undefined) {
                return null;
              }
              return (
                <li
                  key={task.id}
                  ref={virtualizer.measureElement}
                  data-index={row.index}
                  className="absolute left-0 top-0 w-full pb-[var(--pt-space-8)]"
                  style={{ transform: `translateY(${String(row.start)}px)` }}
                >
                  <Card
                    task={task}
                    dimmed={dragging === task.id}
                    onOpen={() => onOpenTask(task.id)}
                    onShift={(direction) => onShift(task, direction)}
                  />
                </li>
              );
            })}
          </ul>
        </SortableContext>

        {items.length === 0 && (
          <p className="py-[var(--pt-space-16)] text-center text-[var(--pt-fs-sm)] text-[var(--pt-text-tertiary)]">
            Пусто
          </p>
        )}

        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-[var(--pt-space-8)] w-full"
            onClick={onMore}
          >
            Показать ещё
          </Button>
        )}
      </div>
    </section>
  );
}

function Card({
  task,
  dimmed,
  onOpen,
  onShift,
}: {
  task: TaskRow;
  dimmed: boolean;
  onOpen: () => void;
  onShift: (direction: -1 | 1) => void;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: task.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "rounded-[var(--pt-radius-3)] border border-[var(--pt-border-default)]",
        "bg-[var(--pt-bg-raised)] p-[var(--pt-space-8)] shadow-[var(--pt-shadow-1)]",
        dimmed && "opacity-50",
      )}
      {...attributes}
      {...listeners}
      onKeyDown={(event) => {
        // Alt+стрелка — то же, что перетащить мышью: доска не должна требовать мыши
        if (event.altKey && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
          event.preventDefault();
          onShift(event.key === "ArrowLeft" ? -1 : 1);
        }
      }}
      title="Alt+← и Alt+→ переносят карточку в соседнюю колонку"
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left text-[var(--pt-font-ui)] hover:underline"
      >
        {task.name}
      </button>
      <div className="mt-[var(--pt-space-4)] flex items-center gap-[var(--pt-space-8)] text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
        <span className="tabular-nums">№{task.number}</span>
        {task.end_at && <span>до {new Date(task.end_at).toLocaleDateString("ru-RU")}</span>}
        {task.comment_count > 0 && (
          <span>{counted(task.comment_count, "запись", "записи", "записей")}</span>
        )}
      </div>
    </div>
  );
}
